import { getGroupNameFromLabel } from "entities/charts/eva/translators/utils";

import { LegendItemModel } from "models";
import { ChartSeries } from "models/model";

import { createLegendItem } from "components/charts/utils";

// A forecast series shares its group with a production series but carries a suffixed
// label (e.g. "4000 - 5000 Forecast (WORKING + EVA Research)" vs "4000 - 5000"), so
// dedup must key off the canonical group name, not the raw label — otherwise the
// forecast variant renders as a second, differently-labeled legend entry for the same
// bin. Preferring the production variant keeps the clean label; if a group only has a
// forecast series, it's still shown (under its canonical name) instead of disappearing.
//
// getGroupNameFromLabel strips everything from the last " Forecast" substring onward,
// so it's only safe to apply to series that are actually forecasts — a production
// label that happens to contain " Forecast" (e.g. an operator named "Forecast Basin
// Energy") must be left untouched, both as the dedupe key and as the display title.
const getGroupName = (item: ChartSeries): string =>
  item.isForecast ? getGroupNameFromLabel(item.label) : item.label;

const dedupeSeriesByGroup = (series: ChartSeries[]): ChartSeries[] => {
  const seriesByGroup = new Map<string, ChartSeries>();
  series.forEach((item) => {
    const groupName = getGroupName(item);
    const existing = seriesByGroup.get(groupName);
    if (!existing || (existing.isForecast && !item.isForecast)) {
      seriesByGroup.set(groupName, item);
    }
  });
  return Array.from(seriesByGroup.values());
};

export const getWellContributionLegendItems = (
  series: ChartSeries[],
  groupByItems?: { title: string; color: string; inFilter: boolean }[],
  isChartFocus?: boolean
): LegendItemModel[] => {
  if (!series) {
    return [];
  }

  const filteredSeries = dedupeSeriesByGroup(
    series.filter((item) => item.label !== "Well Count")
  );

  // If using chart focus, return filtered series without ordering logic
  if (isChartFocus) {
    return filteredSeries.map((s) =>
      createLegendItem(getGroupName(s), true, s.style.hexColor)
    );
  }

  if (groupByItems && groupByItems.length > 0) {
    const orderedItems: LegendItemModel[] = [];
    const remainingItems: LegendItemModel[] = [];

    const seriesMap = new Map(filteredSeries.map((s) => [getGroupName(s), s]));
    groupByItems
      .filter((item) => item.inFilter)
      .forEach((item) => {
        const matchingSeries = seriesMap.get(item.title);
        if (matchingSeries) {
          orderedItems.push(
            createLegendItem(item.title, true, matchingSeries.style.hexColor)
          );
          seriesMap.delete(item.title); // Remove from map to avoid duplicates
        }
      });

    seriesMap.forEach((series, groupName) => {
      remainingItems.push(createLegendItem(groupName, true, series.style.hexColor));
    });

    return [...orderedItems, ...remainingItems];
  }

  return filteredSeries.map((s) =>
    createLegendItem(getGroupName(s), true, s.style.hexColor)
  );
};
